import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@saas-kit/lib';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@saas-kit/ui';

import { getToken } from './apiWorkstation';
import { ForceCallDialog } from './ForceCallDialog';
import { formatWait, secondsSince } from './qmsTime';
import type { QmsCounter, QmsToken } from './types';

type BadgeVariant =
  | 'default'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'destructive'
  | 'outline';

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  waiting: 'secondary',
  called: 'warning',
  recalled: 'warning',
  serving: 'default',
  completed: 'success',
  no_show: 'destructive',
  cancelled: 'destructive',
  transferred: 'outline',
};

interface Props {
  /** The token just issued — used as the initial value while polling. */
  token: QmsToken;
  serviceName: string;
  counters: QmsCounter[];
  canForceCall: boolean;
  canView: boolean;
}

/** Post-issuance action card: live status of the token just issued plus a
 *  shortcut to route it onto a counter — so reception is not left at a
 *  dead end after printing the receipt. */
export function LastIssuedTokenCard({
  token,
  serviceName,
  counters,
  canForceCall,
  canView,
}: Props): JSX.Element {
  const { t } = useTranslation('qms');
  const tenantId = useAuth((s) => s.selectedTenant?.tenant_id);
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [, setTick] = useState(0);

  // Keep the wait age advancing even when the token query is not polling.
  useEffect(() => {
    const timer = window.setInterval(() => setTick((n) => n + 1), 10_000);
    return () => window.clearInterval(timer);
  }, []);

  const tokenQuery = useQuery({
    queryKey: ['qms-issued-token', tenantId, token.id],
    queryFn: () => getToken(token.id),
    enabled: Boolean(tenantId && canView),
    initialData: token,
    refetchInterval: 5_000,
  });
  const live = tokenQuery.data ?? token;

  // The live queue panel polls on its own 5s timer. When this token leaves
  // the waiting state, refetch the queue immediately so it is not shown as
  // both "serving" here and still queued there during the stale window.
  useEffect(() => {
    if (live.status !== 'waiting') {
      void queryClient.invalidateQueries({ queryKey: ['qms-queue', tenantId] });
    }
  }, [live.status, queryClient, tenantId]);

  const isWaiting = live.status === 'waiting';
  const isCalled = live.status === 'called' || live.status === 'recalled';
  const movable = isWaiting || isCalled;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('issue.ops.last_issued')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-2xl font-bold text-foreground">
            {live.display_code}
          </span>
          <Badge variant={STATUS_VARIANT[live.status] ?? 'outline'}>
            {t(`issue.ops.status.${live.status}`, {
              defaultValue: live.status,
            })}
          </Badge>
          {serviceName ? (
            <span className="text-sm text-muted-foreground">
              {serviceName}
            </span>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">
          {t('issue.ops.wait_age', {
            duration: formatWait(secondsSince(live.issued_at)),
          })}
        </p>
        {canForceCall ? (
          <Button
            variant="secondary"
            disabled={!movable}
            onClick={() => setDialogOpen(true)}
          >
            {isCalled
              ? t('issue.ops.change_counter')
              : t('issue.ops.send_to_counter')}
          </Button>
        ) : null}
      </CardContent>
      {canForceCall ? (
        <ForceCallDialog
          open={dialogOpen}
          tokenId={live.id}
          serviceId={live.service_id}
          displayCode={live.display_code}
          counters={counters}
          onClose={() => setDialogOpen(false)}
        />
      ) : null}
    </Card>
  );
}
